import {ChangeEvent, CSSProperties, ReactNode, useEffect, useState} from 'react'
import {useThemeMode} from '../_metronic/partials'
import {KTIcon} from '../_metronic/helpers'

interface SearchBarProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Icon rendered at the start of the field. Defaults to the KTIcon magnifier used across the app. */
  icon?: ReactNode
  /** className applied to the icon wrapper, e.g. to resize/recolor a custom icon. */
  iconClassName?: string
  /** CSS width, e.g. 220, '220px', '100%', 'w-250px' is NOT valid here (use a CSS value). Defaults to 220px, matching the app's toolbar search fields. */
  width?: string | number
  /** Control height in px. Defaults to 38 to match the app's toolbar filter fields. */
  height?: number
  /** Extra className(s) appended to the outer control. */
  className?: string
  /** Font size for the input text/placeholder, e.g. 12 or '14px'. Defaults to 12. */
  fontSize?: number | string
  /** Debounce the onChange call by this many ms. The input stays responsive locally either way. Omit for no debounce (onChange fires on every keystroke). */
  debounceMs?: number
  /** Shows a clear ("x") button once there's text, which resets the field. */
  showClear?: boolean
  disabled?: boolean
  autoFocus?: boolean
  name?: string
  id?: string
}

/**
 * Reusable search input, styled to match the app's toolbar search fields
 * (e.g. ProductsListSearchComponent on /allProducts/list): 38px height,
 * 6px radius, 12px/500 text, KTIcon magnifier. Unlike the toolbar CSS classes
 * it doesn't use `!important`, so width/height are fully controllable via props.
 */
const SearchBar = ({
  value,
  onChange,
  placeholder = 'Search',
  icon,
  iconClassName = '',
  width = 220,
  height = 38,
  className = '',
  fontSize = 12,
  debounceMs,
  showClear = false,
  disabled,
  autoFocus,
  name,
  id,
}: SearchBarProps) => {
  const {mode} = useThemeMode()
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  useEffect(() => {
    if (debounceMs === undefined) return
    if (localValue === value) return
    const timeout = setTimeout(() => onChange(localValue), debounceMs)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue, debounceMs])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    setLocalValue(next)
    if (debounceMs === undefined) onChange(next)
  }

  const handleClear = () => {
    setLocalValue('')
    onChange('')
  }

  const controlStyle: CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    width,
  }

  return (
    <div className={`search-bar-control ${className}`} style={controlStyle}>
      <span className={`search-bar-icon ${iconClassName}`}>
        {icon || <KTIcon iconName='magnifier' className='fs-2' />}
      </span>
      <input
        id={id}
        name={name}
        type='search'
        disabled={disabled}
        autoFocus={autoFocus}
        className={`search-bar-input ${mode === 'dark' ? 'search-bar-input-dark' : ''}`}
        style={{height, fontSize}}
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
      />
      {showClear && localValue && (
        <button type='button' className='search-bar-clear' onClick={handleClear} aria-label='Clear search'>
          <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M18 6 6 18M6 6l12 12' />
          </svg>
        </button>
      )}
    </div>
  )
}

export {SearchBar}
